import React from "react";
import { FileSpreadsheet } from "lucide-react";
import * as XLSX from "xlsx";

export const BULAN_LABEL = ["", "Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

const STATUS_CODE = { hadir: "H", sakit: "S", izin: "I", alfa: "A", bdr: "B" };
const CODE_CLS = {
  H: "text-emerald-700 bg-emerald-50",
  S: "text-sky-700 bg-sky-50",
  I: "text-amber-700 bg-amber-50",
  A: "text-rose-700 bg-rose-50",
  B: "text-violet-700 bg-violet-50",
};

function daysInMonth(bulan, tahun) {
  const y = parseInt(tahun, 10);
  const m = parseInt(bulan, 10);
  if (!y || !m) return 31;
  return new Date(y, m, 0).getDate();
}

function _statusSiswaLabel(v) {
  return v === "aktif" ? "Siswa Aktif" : v === "tidak_aktif" ? "Siswa Tidak Aktif" : "Seluruh Siswa";
}

function _filenameSafe(s) { return String(s).replace(/[^\w-]/g, "_"); }

export function MatrixBulanan({ absensi, siswaAll, bulan, tahun, siswaStatus, siswaSearch }) {
  const dayCount = daysInMonth(bulan, tahun);
  const days = Array.from({ length: dayCount }, (_, i) => i + 1);

  const siswaList = siswaAll
    .filter((s) => {
      const st = s.status || "aktif";
      if (siswaStatus === "aktif" && st !== "aktif") return false;
      if (siswaStatus === "tidak_aktif" && st !== "tidak_aktif") return false;
      return true;
    })
    .filter((s) => !siswaSearch || (s.nama || "").toLowerCase().includes(siswaSearch.toLowerCase()))
    .sort((a, b) => (a.nama || "").localeCompare(b.nama || ""));

  const dataMap = {};
  for (const a of absensi) {
    const t = a.tanggal || "";
    if (t.slice(0, 4) !== tahun) continue;
    if (t.slice(5, 7) !== bulan) continue;
    const d = parseInt(t.slice(8, 10), 10);
    if (!dataMap[a.siswa_id]) dataMap[a.siswa_id] = {};
    dataMap[a.siswa_id][d] = a.status;
  }

  const perStudent = {};
  const perDay = Object.fromEntries(days.map((d) => [d, { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 }]));
  for (const s of siswaList) {
    const t = { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 };
    const cells = dataMap[s.id] || {};
    for (const d of days) {
      const st = cells[d];
      if (!st) continue;
      if (t[st] !== undefined) t[st] += 1;
      if (perDay[d][st] !== undefined) perDay[d][st] += 1;
    }
    perStudent[s.id] = t;
  }

  return (
    <section className="pp-card pp-freeze overflow-hidden" data-testid="rekap-matrix">
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 text-sm text-slate-700 flex flex-wrap items-center gap-3">
        <div>
          <b>Rekap Bulanan — {BULAN_LABEL[parseInt(bulan, 10)] || bulan} {tahun}</b>
          <span className="ml-2 text-xs text-slate-500">({_statusSiswaLabel(siswaStatus)} · {siswaList.length} siswa)</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-slate-500">Legenda: H=Hadir · S=Sakit · I=Izin · A=Alpa · B=BDR</span>
          <button
            onClick={() => exportBulananXlsx({ siswaList, dataMap, perDay, perStudent, days, bulan, tahun, siswaStatus })}
            className="pp-btn-secondary text-xs"
            data-testid="export-xlsx-bulanan"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Download Excel
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse" data-testid="rekap-matrix-table">
          <thead>
            <tr className="bg-slate-100 text-slate-700">
              <th className="sticky left-0 z-10 bg-slate-100 border border-slate-200 px-2 py-2 text-left w-10">No</th>
              <th className="sticky left-10 z-10 bg-slate-100 border border-slate-200 px-3 py-2 text-left min-w-[200px]">Nama Siswa</th>
              {days.map((d) => <th key={d} className="border border-slate-200 px-1 py-2 text-center w-8 font-mono">{d}</th>)}
              <th className="border border-slate-200 px-1.5 py-2 text-center bg-emerald-100 text-emerald-800 w-10">H</th>
              <th className="border border-slate-200 px-1.5 py-2 text-center bg-sky-100 text-sky-800 w-10">S</th>
              <th className="border border-slate-200 px-1.5 py-2 text-center bg-amber-100 text-amber-800 w-10">I</th>
              <th className="border border-slate-200 px-1.5 py-2 text-center bg-rose-100 text-rose-800 w-10">A</th>
              <th className="border border-slate-200 px-1.5 py-2 text-center bg-violet-100 text-violet-800 w-10">B</th>
            </tr>
          </thead>
          <tbody>
            {siswaList.length === 0 && (
              <tr><td colSpan={days.length + 7} className="text-center py-10 text-slate-400 italic">Tidak ada siswa untuk filter ini.</td></tr>
            )}
            {siswaList.map((s, idx) => {
              const cells = dataMap[s.id] || {};
              const t = perStudent[s.id];
              return (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="sticky left-0 bg-white border border-slate-200 px-2 py-1.5 text-center font-mono">{idx + 1}</td>
                  <td className={`sticky left-10 bg-white border border-slate-200 px-3 py-1.5 whitespace-nowrap ${s.status === "tidak_aktif" ? "text-slate-400 italic" : ""}`}>{s.nama}</td>
                  {days.map((d) => {
                    const st = cells[d];
                    const code = st ? STATUS_CODE[st] : "";
                    return (
                      <td key={d} className={`border border-slate-200 text-center font-mono py-1 ${code ? CODE_CLS[code] : "bg-slate-50 text-slate-300"}`}>{code || "—"}</td>
                    );
                  })}
                  <td className="border border-slate-200 text-center py-1 font-semibold text-emerald-700">{t.hadir}</td>
                  <td className="border border-slate-200 text-center py-1 font-semibold text-sky-700">{t.sakit}</td>
                  <td className="border border-slate-200 text-center py-1 font-semibold text-amber-700">{t.izin}</td>
                  <td className="border border-slate-200 text-center py-1 font-semibold text-rose-700">{t.alfa}</td>
                  <td className="border border-slate-200 text-center py-1 font-semibold text-violet-700">{t.bdr}</td>
                </tr>
              );
            })}
            {siswaList.length > 0 && (
              <tr className="bg-slate-100 font-semibold text-slate-800">
                <td className="sticky left-0 bg-slate-100 border border-slate-200 px-2 py-2 text-center" colSpan={2}>JUMLAH HADIR</td>
                {days.map((d) => <td key={d} className="border border-slate-200 text-center py-1.5 font-mono">{perDay[d].hadir}</td>)}
                <td className="border border-slate-200 text-center py-1.5" colSpan={5}>—</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function MatrixTahunan({ absensi, siswaAll, tahun, siswaStatus, siswaSearch }) {
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const statuses = [
    { k: "hadir", code: "H", cls: "text-emerald-700" },
    { k: "sakit", code: "S", cls: "text-sky-700" },
    { k: "izin", code: "I", cls: "text-amber-700" },
    { k: "alfa", code: "A", cls: "text-rose-700" },
    { k: "bdr", code: "B", cls: "text-violet-700" },
  ];

  const siswaList = siswaAll
    .filter((s) => {
      const st = s.status || "aktif";
      if (siswaStatus === "aktif" && st !== "aktif") return false;
      if (siswaStatus === "tidak_aktif" && st !== "tidak_aktif") return false;
      return true;
    })
    .filter((s) => !siswaSearch || (s.nama || "").toLowerCase().includes(siswaSearch.toLowerCase()))
    .sort((a, b) => (a.nama || "").localeCompare(b.nama || ""));

  const dataMap = {};
  for (const a of absensi) {
    const t = a.tanggal || "";
    if (t.slice(0, 4) !== tahun) continue;
    const m = parseInt(t.slice(5, 7), 10);
    if (!m) continue;
    if (!dataMap[a.siswa_id]) dataMap[a.siswa_id] = {};
    if (!dataMap[a.siswa_id][m]) dataMap[a.siswa_id][m] = { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 };
    if (dataMap[a.siswa_id][m][a.status] !== undefined) dataMap[a.siswa_id][m][a.status] += 1;
  }

  return (
    <section className="pp-card pp-freeze overflow-hidden" data-testid="rekap-matrix-tahunan">
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 text-sm text-slate-700 flex flex-wrap items-center gap-3">
        <div>
          <b>Rekap Tahunan — {tahun}</b>
          <span className="ml-2 text-xs text-slate-500">({_statusSiswaLabel(siswaStatus)} · {siswaList.length} siswa)</span>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-slate-500">Legenda: H=Hadir · S=Sakit · I=Izin · A=Alpa · B=BDR</span>
          <button
            onClick={() => exportTahunanXlsx({ siswaList, dataMap, tahun, siswaStatus })}
            className="pp-btn-secondary text-xs"
            data-testid="export-xlsx-tahunan"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Download Excel
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs border-collapse" data-testid="rekap-matrix-tahunan-table">
          <thead>
            <tr className="bg-slate-100 text-slate-700">
              <th rowSpan={2} className="sticky left-0 z-20 bg-slate-100 border border-slate-200 px-2 py-2 text-center w-10 align-middle">No</th>
              <th rowSpan={2} className="sticky left-10 z-20 bg-slate-100 border border-slate-200 px-3 py-2 text-left min-w-[200px] align-middle">Nama Siswa</th>
              {months.map((m) => (
                <th key={m} colSpan={5} className="border border-slate-200 px-1 py-1.5 text-center bg-indigo-50 text-indigo-900">{BULAN_LABEL[m].slice(0, 3)}</th>
              ))}
              <th colSpan={5} className="border border-slate-200 px-1 py-1.5 text-center bg-slate-200 text-slate-900">TOTAL</th>
            </tr>
            <tr className="bg-slate-100 text-slate-700 text-[10px]">
              {months.map((m) => (statuses.map((st) => (
                <th key={`${m}-${st.code}`} className={`border border-slate-200 px-1 py-1 text-center w-7 font-mono ${st.cls}`}>{st.code}</th>
              ))))}
              {statuses.map((st) => (
                <th key={`t-${st.code}`} className={`border border-slate-200 px-1 py-1 text-center w-8 font-mono ${st.cls} bg-slate-50`}>{st.code}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {siswaList.length === 0 && (
              <tr><td colSpan={2 + 12 * 5 + 5} className="text-center py-10 text-slate-400 italic">Tidak ada siswa untuk filter ini.</td></tr>
            )}
            {siswaList.map((s, idx) => {
              const rowData = dataMap[s.id] || {};
              const totals = { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 };
              return (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="sticky left-0 bg-white border border-slate-200 px-2 py-1 text-center font-mono">{idx + 1}</td>
                  <td className={`sticky left-10 bg-white border border-slate-200 px-3 py-1 whitespace-nowrap ${s.status === "tidak_aktif" ? "text-slate-400 italic" : ""}`}>{s.nama}</td>
                  {months.map((m) => {
                    const cell = rowData[m] || { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 };
                    return statuses.map((st) => {
                      totals[st.k] += cell[st.k];
                      const v = cell[st.k];
                      return (
                        <td key={`${s.id}-${m}-${st.code}`} className={`border border-slate-200 text-center py-1 font-mono ${v ? st.cls : "text-slate-300"}`}>{v || "—"}</td>
                      );
                    });
                  })}
                  {statuses.map((st) => (
                    <td key={`${s.id}-t-${st.code}`} className={`border border-slate-200 text-center py-1 font-mono font-bold ${st.cls} bg-slate-50`}>{totals[st.k]}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ══════ EXCEL EXPORTS ══════ */
function _applyColWidths(ws, widths) { ws["!cols"] = widths.map((wch) => ({ wch })); }

function exportBulananXlsx({ siswaList, dataMap, perDay, perStudent, days, bulan, tahun, siswaStatus }) {
  const nBulan = BULAN_LABEL[parseInt(bulan, 10)] || bulan;
  const header1 = ["No", "Nama Siswa", ...days.map((d) => String(d)), "H", "S", "I", "A", "B"];
  const rows = [
    [`Rekap Absensi Bulanan — ${nBulan} ${tahun}`],
    [`${_statusSiswaLabel(siswaStatus)} · ${siswaList.length} siswa`],
    [],
    header1,
  ];
  siswaList.forEach((s, idx) => {
    const cells = dataMap[s.id] || {};
    const t = perStudent[s.id] || { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 };
    const row = [idx + 1, s.nama];
    for (const d of days) row.push(cells[d] ? STATUS_CODE[cells[d]] : "");
    row.push(t.hadir, t.sakit, t.izin, t.alfa, t.bdr);
    rows.push(row);
  });
  const jhRow = ["", "JUMLAH HADIR"];
  for (const d of days) jhRow.push(perDay[d].hadir);
  jhRow.push("", "", "", "", "");
  rows.push(jhRow);
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const totalCols = header1.length;
  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } },
  ];
  _applyColWidths(ws, [5, 28, ...days.map(() => 4), 5, 5, 5, 5, 5]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Bulanan ${nBulan}`.slice(0, 31));
  XLSX.writeFile(wb, `rekap-absensi-bulanan-${_filenameSafe(nBulan)}-${tahun}.xlsx`);
}

function exportTahunanXlsx({ siswaList, dataMap, tahun, siswaStatus }) {
  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const codes = ["H", "S", "I", "A", "B"];
  const keys = ["hadir", "sakit", "izin", "alfa", "bdr"];
  const h1 = ["No", "Nama Siswa"];
  for (const m of months) { h1.push(BULAN_LABEL[m].slice(0, 3)); h1.push(""); h1.push(""); h1.push(""); h1.push(""); }
  h1.push("TOTAL"); h1.push(""); h1.push(""); h1.push(""); h1.push("");
  const h2 = ["", ""];
  for (const _ of months) codes.forEach((c) => h2.push(c));
  codes.forEach((c) => h2.push(c));
  const rows = [
    [`Rekap Absensi Tahunan — ${tahun}`],
    [`${_statusSiswaLabel(siswaStatus)} · ${siswaList.length} siswa`],
    [],
    h1,
    h2,
  ];
  siswaList.forEach((s, idx) => {
    const rowData = dataMap[s.id] || {};
    const totals = { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 };
    const row = [idx + 1, s.nama];
    for (const m of months) {
      const cell = rowData[m] || { hadir: 0, sakit: 0, izin: 0, alfa: 0, bdr: 0 };
      keys.forEach((k) => { row.push(cell[k] || 0); totals[k] += (cell[k] || 0); });
    }
    keys.forEach((k) => row.push(totals[k]));
    rows.push(row);
  });
  const ws = XLSX.utils.aoa_to_sheet(rows);
  const totalCols = h1.length;
  const merges = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: totalCols - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: totalCols - 1 } },
  ];
  months.forEach((_, i) => {
    const c0 = 2 + i * 5;
    merges.push({ s: { r: 3, c: c0 }, e: { r: 3, c: c0 + 4 } });
  });
  merges.push({ s: { r: 3, c: 2 + 12 * 5 }, e: { r: 3, c: 2 + 12 * 5 + 4 } });
  merges.push({ s: { r: 3, c: 0 }, e: { r: 4, c: 0 } });
  merges.push({ s: { r: 3, c: 1 }, e: { r: 4, c: 1 } });
  ws["!merges"] = merges;
  _applyColWidths(ws, [5, 28, ...Array(12 * 5).fill(4), ...Array(5).fill(5)]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, `Tahunan ${tahun}`.slice(0, 31));
  XLSX.writeFile(wb, `rekap-absensi-tahunan-${tahun}.xlsx`);
}
